import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  computed,
  inject,
  input
} from '@angular/core';
import anime from 'animejs/lib/anime.es.js';

/**
 * Decorative sparkle cluster. `welcome` adds a 3rd sparkle and more spread.
 *
 * Animation follows the design-system convention (`m-fade-in-up`,
 * `m-count-up`): anime.js call inside `ngAfterViewInit`. Per-sparkle
 * delay uses `anime.stagger`, so DOM order encodes the delay sequence
 * (0ms, 600ms, 1200ms). Sparkles are absolute-positioned, so DOM order
 * is purely an animation-sequencing concern and does not affect layout.
 */
@Component({
  selector: 'portal-sparkle-cluster',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sparkle-cluster.component.html'
})
export class PortalSparkleClusterComponent implements AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef);

  @HostBinding('class') readonly hostClass = 'contents';

  readonly variant = input<'welcome' | 'header'>('header');
  protected readonly isWelcome = computed(() => this.variant() === 'welcome');

  protected readonly containerClass = computed(() =>
    this.isWelcome()
      ? 'relative w-[3.75rem] h-[3.25rem] mb-5'
      : 'relative w-12 h-10'
  );

  ngAfterViewInit(): void {
    anime({
      targets: this.sparkles(),
      opacity: [1, 0.55],
      scale: [1, 1.08],
      filter: [
        'drop-shadow(0 0 2px rgba(249, 115, 22, 0.4))',
        'drop-shadow(0 0 6px rgba(249, 115, 22, 0.6))'
      ],
      duration: 1500,
      easing: 'easeInOutSine',
      direction: 'alternate',
      delay: anime.stagger(600),
      loop: true
    });
  }

  ngOnDestroy(): void {
    // Stop the loop when the panel closes; otherwise anime keeps a
    // reference to the (now-detached) <img> nodes.
    anime.remove(this.sparkles());
  }

  private sparkles(): NodeListOf<HTMLImageElement> {
    return this.el.nativeElement.querySelectorAll('img.sparkle');
  }
}
